import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { MessageCircle, Smartphone, Zap } from 'lucide-react';

export const WhatsAppModal = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    inquiryType: 'general',
    message: '',
    preferredContactTime: '',
    acceptTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const industries = [
    'Spielhalle',
    'Wettbüro',
    'Gastronomie mit Geldspielgeräten',
    'Veranstaltungen',
    'Casino',
    'Sonstige'
  ];

  const inquiryTypes = [
    { value: 'general', label: 'Allgemeine Informationen' },
    { value: 'pricing', label: 'Preise und Pakete' },
    { value: 'demo', label: 'Demo-Termin vereinbaren' },
    { value: 'support', label: 'Technischer Support' },
    { value: 'integration', label: 'Integration & Setup' },
    { value: 'enterprise', label: 'Enterprise-Lösung' }
  ];

  const contactTimes = [
    'Sofort',
    'Vormittags (9-12 Uhr)',
    'Nachmittags (12-17 Uhr)',
    'Abends (17-19 Uhr)',
    'Morgen',
    'Diese Woche'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Mock API call - wird später durch Make.com Webhook ersetzt
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Hier würde die Make.com Integration erfolgen:
      // await fetch('https://hook.eu1.make.com/your-whatsapp-webhook', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     type: 'whatsapp_inquiry',
      //     data: formData,
      //     timestamp: new Date().toISOString()
      //   })
      // });

      // WhatsApp Chat mit vorausgefüllter Nachricht öffnen
      const whatsappMessage = `Hallo! Ich bin ${formData.firstName} ${formData.lastName} von ${formData.company}. ${formData.message || 'Ich interessiere mich für IDA KI und hätte gerne weitere Informationen.'}`;
      const whatsappUrl = `https://wa.me/4917623943896?text=${encodeURIComponent(whatsappMessage)}`;
      
      alert('Daten erfasst! Sie werden nun zu WhatsApp weitergeleitet.');
      window.open(whatsappUrl, '_blank');
      
      setIsOpen(false);
      setFormData({
        firstName: '', lastName: '', email: '', phone: '', company: '',
        industry: '', inquiryType: 'general', message: '', preferredContactTime: '',
        acceptTerms: false
      });
    } catch (error) {
      alert('Fehler beim Verarbeiten der Anfrage. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-green-500" />
            WhatsApp Business Chat
          </DialogTitle>
          <DialogDescription>
            Starten Sie einen direkten Chat mit unserem Team. Wir antworten schnell und kompetent auf Ihre Fragen.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          {/* Kontaktdaten */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">Vorname *</Label>
              <Input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="lastName">Nachname *</Label>
              <Input
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">E-Mail-Adresse *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="phone">WhatsApp-Nummer *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="mt-1"
                placeholder="+49 xxx xxxxxxxx"
              />
            </div>
          </div>

          {/* Unternehmensdaten */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-green-500" />
              Unternehmensdaten
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company">Unternehmensname *</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="industry">Branche</Label>
                <Select value={formData.industry} onValueChange={(value) => handleSelectChange('industry', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Wählen Sie Ihre Branche" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Anfrage-Details */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-green-500" />
              Ihre Anfrage
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="inquiryType">Art der Anfrage</Label>
                <Select value={formData.inquiryType} onValueChange={(value) => handleSelectChange('inquiryType', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {inquiryTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="preferredContactTime">Bevorzugte Kontaktzeit</Label>
                <Select value={formData.preferredContactTime} onValueChange={(value) => handleSelectChange('preferredContactTime', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Wann sollen wir uns melden?" />
                  </SelectTrigger>
                  <SelectContent>
                    {contactTimes.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="message">Nachricht</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                className="mt-1"
                placeholder="Beschreiben Sie Ihr Anliegen oder stellen Sie Ihre Fragen..."
                rows={4}
              />
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <MessageCircle className="w-5 h-5 text-green-600 mt-1" />
              <div>
                <h4 className="text-sm font-semibold text-green-800">So funktioniert es:</h4>
                <p className="text-sm text-green-700 mt-1">
                  Nach dem Absenden werden Sie direkt zu WhatsApp weitergeleitet mit einer vorausgefüllten Nachricht. 
                  Unser Team antwortet in der Regel innerhalb von 15 Minuten während der Geschäftszeiten.
                </p>
              </div>
            </div>
          </div>

          {/* Rechtliches */}
          <div className="border-t pt-6">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="acceptTermsWhatsApp"
                checked={formData.acceptTerms}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, acceptTerms: checked }))}
                className="mt-1"
              />
              <Label htmlFor="acceptTermsWhatsApp" className="text-sm leading-relaxed">
                Ich akzeptiere die{' '}
                <a href="/agb" target="_blank" className="text-teal-600 hover:text-teal-700 font-medium">AGB</a>
                {' '}und stimme der Kontaktaufnahme über WhatsApp sowie Verarbeitung meiner Daten zu.
              </Label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="border-t pt-6 flex justify-end space-x-3">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsOpen(false)}
              disabled={isSubmitting}
            >
              Abbrechen
            </Button>
            <Button 
              type="submit" 
              className="bg-green-500 hover:bg-green-600"
              disabled={isSubmitting || !formData.acceptTerms}
            >
              {isSubmitting ? 'Öffne WhatsApp...' : 'WhatsApp Chat starten'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};